/**
 * Config de runtime de los frontends (white-label + endpoints publicos).
 *
 * La API sirve `/config.js` (cargado de forma bloqueante en el <head>, antes
 * del bundle) que define `window.__APP_CONFIG__` a partir de un archivo
 * montado en el contenedor. Asi la marca y las URLs publicas NO se compilan:
 * una misma imagen sirve a cualquier cliente/entorno y sin parpadeo de tema.
 *
 * Capas (la ultima gana): brand.json del bundle -> window.__APP_CONFIG__.
 * URLs: window.__APP_CONFIG__ -> VITE_* (dev local y compatibilidad).
 *
 * Cada sitio (app, afiliados) importa su brand.json y llama a `buildAppConfig`.
 */

export type DeepPartial<T> = { [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K] };

export interface RuntimeConfig<B = Record<string, unknown>> {
  brand?: DeepPartial<B>;
  apiUrl?: string;
  supabaseUrl?: string;
  supabaseAnonKey?: string;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function deepMerge<T>(base: T, over: unknown): T {
  if (!isPlainObject(base) || !isPlainObject(over)) return base;
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(over)) {
    out[k] = isPlainObject(v) && isPlainObject(out[k]) ? deepMerge(out[k], v) : v;
  }
  return out as T;
}

export function buildAppConfig<B>(
  brandDefault: B,
  runtime: RuntimeConfig<B> | undefined,
  env: Record<string, string | undefined>,
) {
  return {
    brand: deepMerge(brandDefault, runtime?.brand) as B,
    apiUrl: runtime?.apiUrl || env.VITE_API_URL || '/api',
    supabaseUrl: runtime?.supabaseUrl || env.VITE_SUPABASE_URL || '',
    supabaseAnonKey: runtime?.supabaseAnonKey || env.VITE_SUPABASE_ANON_KEY || '',
  };
}
